import React, { Fragment, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import { CircularProgress, Grid } from "@mui/material"
import { NotificationUtils } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { KonanTabsHeader } from "../../components/core/KonanTabsHeader"
import { KonanEmptyState, KonanEmptyStateSearch } from "../../components/utility/KonanEmptyState"
import { useHandleTrainingQueryParam } from "../../hooks/useHandleTrainingQueryParam"
import { KonanAPI } from "../../services/KonanAPI"
import type { RootState } from "../../store/ReduxStore"
import type { CreateRetrainingModelRequest, TrainingAndRetraining, baseErrorType } from "../../types/custom/projects"
import type { AutoMLTrainingJob } from "../../types/generated/api/AutoMLTrainingJob"
import type { PaginatedAutoMLTrainingJobList } from "../../types/generated/api/PaginatedAutoMLTrainingJobList"
import type { PaginatedModelListCreateList } from "../../types/generated/api/PaginatedModelListCreateList"
import type { PaginatedRetrainingJobList } from "../../types/generated/api/PaginatedRetrainingJobList"
import type { PaginatedTrainingJobList } from "../../types/generated/api/PaginatedTrainingJobList"
import type { RetrainingJob } from "../../types/generated/api/RetrainingJob"
import type { TrainingJob } from "../../types/generated/api/TrainingJob"
import { type StandardErrorType, withErrorHandler } from "../../utils/ErrorHandling"
import { allJobsList, mapResponseTypesToTabValues, trainingJobList } from "../../utils/deploymentDetailsHelpers"
import { getModelByUUID } from "../../utils/modelDetailsHelpers"
import { searchRetrainingsByName, sortRetrainings } from "../../utils/searchSortFilterHelpers"
import { PendingFailedTrainingCardLoader } from "./components/PendingFailedTrainingCardLoader"
import { PendingFailedTrainingJobCard } from "./components/PendingFailedTrainingJobCard"
import { RetrainingCreationDialog } from "./components/RetrainingCreationCard"
import { SuccessfulTrainingCardLoader } from "./components/SuccessfulTrainingCardLoader"
import { SuccessfulTrainingJobCard } from "./components/SuccessfulTrainingJobCard"

import styles from "./TrainingJobs.module.scss"

export function TrainingJobs(): React.ReactElement {
  const { id: projectId } = useParams<{ id: string }>()
  const { t } = useTranslation("TrainingJob")

  const currentModel = useSelector((state: RootState) => state.models.currentModel)
  const currentProject = useSelector((state: RootState) => state.project.currentProject)

  // Configure intersection observer for infinite scroll functionality
  // Only triggers next page fetch when last item is in view and more data is available
  const { ref: lastTrainingJobRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasJobsLeft && !isTrainingJobLoading && loadMoreJobs()
    },
  })

  const queryClient = useQueryClient()

  const cleanType = currentProject?.uuid ? currentProject?.type?.replace(/_+/g, " ") : ""
  const pageSize = 6

  let allSucceededTrainingJobs: (TrainingAndRetraining | TrainingJob | RetrainingJob | AutoMLTrainingJob)[] = []
  let allFailedTrainingJobs: (TrainingAndRetraining | TrainingJob | RetrainingJob | AutoMLTrainingJob)[] = []
  let allPendingTrainingJobs: (TrainingAndRetraining | TrainingJob | RetrainingJob | AutoMLTrainingJob)[] = []

  const [newTrainingJobOpen, setNewTrainingJobOpen] = useState<boolean>(false)
  const [searchValue, setSearchValue] = useState<string>("")
  const [sortValue, setSortValue] = useState<string>("Most Recent")
  const [sortValueFailed, setSortValueFailed] = useState<string>("Most Recent")
  const [searchValueFailed, setSearchValueFailed] = useState<string>("")
  const [sortValueInProgress, setSortValueInProgress] = useState<string>("Most Recent")
  const [searchValueInProgress, setSearchValueInProgress] = useState<string>("")
  const [tabValue, setTabValue] = useHandleTrainingQueryParam(["success", "failed", "inProgress"], "success")
  const [errorMessage, setErrorMessage] = useState<string>()
  const [refetchInterval, setRefetchInterval] = useState<number | false | undefined>(false)
  const [currentPage, setCurrentPage] = useState(1)

  const { data: models } = useQuery<AxiosResponse<PaginatedModelListCreateList>, AxiosError<baseErrorType>>(
    ["overviewModels", projectId],
    () => KonanAPI.fetchModels({ projectId: projectId as string, pageSize: 1000 }),
  )

  // fetch automl training jobs
  const { isLoading: isTrainingAutoMlLoading, data: trainingAutoMlModels } = useQuery<
    PaginatedAutoMLTrainingJobList,
    AxiosError<baseErrorType>
  >(["training-jobs-automl", projectId], () => KonanAPI.fetchAutoMlTrainingJobs(projectId as string, 100, 1), {
    enabled: !!projectId,
    refetchInterval: refetchInterval,
    refetchIntervalInBackground: true,
    onError: () => {
      setRefetchInterval(false)
    },
    onSuccess: (data) => {
      if (
        (data?.results && data?.results[0]?.status === "PENDING") ||
        (data?.results && data?.results[0]?.status === "RUNNING")
      ) {
        return setRefetchInterval(3000)
      } else setRefetchInterval(false)
    },
  })

  // fetch re-training jobs
  const { isLoading: isRetrainigLoading, data: retrainingModels } = useQuery<
    PaginatedRetrainingJobList,
    AxiosError<baseErrorType>
  >(["retraining-jobs", projectId], () => KonanAPI.fetchRetrainingModels(projectId as string, 100, 1), {
    enabled: !!projectId,
    refetchInterval: refetchInterval,
    refetchIntervalInBackground: true,
    onError: () => {
      setRefetchInterval(false)
    },
    onSuccess: (data) => {
      if (
        (data?.results && data?.results[0]?.status === "PENDING") ||
        (data?.results && data?.results[0]?.status === "RUNNING")
      ) {
        return setRefetchInterval(3000)
      } else setRefetchInterval(false)
    },
  })

  const { isLoading: isTrainingLoading, data: trainingModels } = useQuery<
    PaginatedTrainingJobList,
    AxiosError<StandardErrorType>
  >(["training-jobs", projectId], () => KonanAPI.fetchTrainingModels(projectId as string, 100, 1), {
    enabled: !!projectId,
    refetchInterval: refetchInterval,
    refetchIntervalInBackground: true,
    onError: withErrorHandler(() => {
      setRefetchInterval(false)
    }),
    onSuccess: (data) => {
      if (
        (data?.results && data?.results[0]?.status === "PENDING") ||
        (data?.results && data?.results[0]?.status === "RUNNING")
      ) {
        return setRefetchInterval(3000)
      } else setRefetchInterval(false)
    },
  })

  // FILTERING ALL SUCCEEDED TRAINING + RETRAINING JOBS
  const succeededTrainingJobs = trainingJobList(
    "success",
    trainingModels?.results !== undefined && trainingAutoMlModels?.results !== undefined
      ? [...trainingModels?.results, ...trainingAutoMlModels?.results]
      : undefined,
    true,
  )

  const succeededRetrainingJobs = trainingJobList("success", retrainingModels?.results, false)
  allSucceededTrainingJobs = allJobsList(succeededTrainingJobs, succeededRetrainingJobs)
  // FILTERING ALL FAILED TRAINING + RETRAINING JOBS
  const failedTrainingJobs = trainingJobList(
    "failed",
    trainingModels?.results !== undefined && trainingAutoMlModels?.results !== undefined
      ? [...trainingModels?.results, ...trainingAutoMlModels?.results]
      : undefined,
    true,
  )

  const failedRetrainingJobs = trainingJobList("failed", retrainingModels?.results, false)
  allFailedTrainingJobs = allJobsList(failedTrainingJobs, failedRetrainingJobs)
  // FILTERING ALL SUCCEEDED TRAINING + RETRAINING JOBS
  const pendingTrainingJobs = trainingJobList(
    "pending",
    trainingModels?.results !== undefined && trainingAutoMlModels?.results !== undefined
      ? [...trainingModels?.results, ...trainingAutoMlModels?.results]
      : undefined,
    true,
  )

  const pendingRetrainingJobs = trainingJobList("pending", retrainingModels?.results, false)
  allPendingTrainingJobs = allJobsList(pendingTrainingJobs, pendingRetrainingJobs)

  type extendedTrainingJobs = (TrainingAndRetraining | TrainingJob | RetrainingJob | AutoMLTrainingJob) & {
    current_model_state: string
  }

  const adjustedAllSuccessfulTrainingJobs = useMemo(
    (): extendedTrainingJobs[] => {
      let adjustedTrainings = allSucceededTrainingJobs || []
      if (searchValue && searchValue?.length > 0)
        adjustedTrainings = adjustedTrainings ? searchRetrainingsByName(adjustedTrainings, searchValue) : []

      if (sortValue && sortValue?.length > 0)
        adjustedTrainings = adjustedTrainings ? sortRetrainings(adjustedTrainings, sortValue) : []

      adjustedTrainings = adjustedTrainings.map((item) => ({
        ...item,
        current_model_state: getModelByUUID(item.resulting_model ?? "", models?.data.results)?.state,
      }))

      return adjustedTrainings as extendedTrainingJobs[]
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      allSucceededTrainingJobs,
      searchValue,
      sortValue,
      trainingModels?.results?.length,
      retrainingModels?.results?.length,
    ],
  )

  const adjustedAllPendingTrainingJobs = useMemo(
    (): (TrainingAndRetraining | TrainingJob | RetrainingJob | AutoMLTrainingJob)[] => {
      let adjustedTrainings = allPendingTrainingJobs || []
      if (searchValueInProgress && searchValueInProgress?.length > 0)
        adjustedTrainings = adjustedTrainings ? searchRetrainingsByName(adjustedTrainings, searchValueInProgress) : []

      if (sortValueInProgress && sortValueInProgress?.length > 0)
        adjustedTrainings = adjustedTrainings ? sortRetrainings(adjustedTrainings, sortValueInProgress) : []

      return adjustedTrainings
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      allPendingTrainingJobs,
      searchValueInProgress,
      sortValueInProgress,
      trainingModels?.results?.length,
      retrainingModels?.results?.length,
      refetchInterval,
    ],
  )

  const adjustedAllFailedTrainingJobs = useMemo(
    (): (TrainingAndRetraining | TrainingJob | RetrainingJob | AutoMLTrainingJob)[] => {
      let adjustedTrainings = allFailedTrainingJobs || []
      if (searchValueFailed && searchValueFailed?.length > 0)
        adjustedTrainings = adjustedTrainings ? searchRetrainingsByName(adjustedTrainings, searchValueFailed) : []

      if (sortValueFailed && sortValueFailed?.length > 0)
        adjustedTrainings = adjustedTrainings ? sortRetrainings(adjustedTrainings, sortValueFailed) : []

      return adjustedTrainings
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      allFailedTrainingJobs,
      searchValueFailed,
      sortValueFailed,
      trainingModels?.results?.length,
      retrainingModels?.results?.length,
    ],
  )

  const isTrainingJobLoading = isTrainingLoading || isRetrainigLoading || isTrainingAutoMlLoading

  const hasJobsLeft =
    (tabValue === "success" &&
      adjustedAllSuccessfulTrainingJobs &&
      adjustedAllSuccessfulTrainingJobs.length > currentPage * pageSize) ||
    (tabValue === "failed" &&
      adjustedAllFailedTrainingJobs &&
      adjustedAllFailedTrainingJobs.length > currentPage * pageSize) ||
    (tabValue === "inProgress" &&
      adjustedAllPendingTrainingJobs &&
      adjustedAllPendingTrainingJobs.length > currentPage * pageSize)

  const createRetrainingModelMutation = useMutation<
    AxiosResponse<RetrainingJob>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AxiosError<any>,
    CreateRetrainingModelRequest
  >(KonanAPI.createRetrainingModel)

  const handleRetrainingModelCreation = (
    predictions_start_time: string,
    predictions_end_time: string,
    append_training_data: boolean,
    resulting_model_name: string,
    resulting_model_state: string,
  ): void => {
    createRetrainingModelMutation
      .mutateAsync({
        predictions_start_time,
        predictions_end_time,
        append_training_data,
        resulting_model_name,
        resulting_model_state,
        project_uuid: projectId as string,
      })
      .then((data) => {
        setNewTrainingJobOpen(false)
        queryClient.invalidateQueries(["models", currentModel?.uuid])
        queryClient.invalidateQueries(["training-jobs", projectId])
        queryClient.invalidateQueries(["training-jobs-automl", projectId])
        queryClient.invalidateQueries(["retraining-jobs", currentModel?.uuid])
        setTimeout(() => {
          const lowerCaseType = data?.data?.status?.toLowerCase()
          setTabValue(mapResponseTypesToTabValues(lowerCaseType))
        }, 500)
        NotificationUtils.toast(t("notifications.retrainingJobProcessed"), { severity: "success" })
      })
  }

  const loadMoreJobs = (): void => {
    setCurrentPage(currentPage + 1)
  }

  useEffect(() => {
    setCurrentPage(1)
  }, [tabValue])

  useEffect(() => {
    if (createRetrainingModelMutation.error) {
      setErrorMessage(createRetrainingModelMutation.error?.response?.data.detail)
    }
  }, [createRetrainingModelMutation.error])
  const handleCreationDialogClose = (): void => {
    setNewTrainingJobOpen(false)
    setErrorMessage(undefined)
  }

  useEffect(() => {
    createRetrainingModelMutation.error?.response?.data?.resulting_model_name &&
      NotificationUtils.toast(createRetrainingModelMutation.error?.response?.data?.resulting_model_name[0], {
        severity: "error",
      })
  }, [createRetrainingModelMutation.error?.response?.data?.resulting_model_name])

  return (
    <Fragment>
      {newTrainingJobOpen && (
        <RetrainingCreationDialog
          onModelCreation={handleRetrainingModelCreation}
          retrainingError={errorMessage}
          onClose={handleCreationDialogClose}
          open={newTrainingJobOpen}
          setOpen={setNewTrainingJobOpen}
          isLoading={createRetrainingModelMutation.isLoading}
        />
      )}
      <Grid container item pt={2}>
        <KonanTabsHeader
          title={t("empty_states.success.buttonText")}
          setAction={setNewTrainingJobOpen}
          containerType={"Training Job"}
          setTabValue={setTabValue}
          value={tabValue}
          searchValue={
            tabValue === "success" ? searchValue : tabValue === "failed" ? searchValueFailed : searchValueInProgress
          }
          setSearchValue={
            tabValue === "success"
              ? setSearchValue
              : tabValue === "failed"
                ? setSearchValueFailed
                : setSearchValueInProgress
          }
          sortValue={tabValue === "success" ? sortValue : tabValue === "failed" ? sortValueFailed : sortValueInProgress}
          setSortValue={
            tabValue === "success" ? setSortValue : tabValue === "failed" ? setSortValueFailed : setSortValueInProgress
          }
          sortOptions={[
            { label: t("sort.mostRecent"), value: "Most Recent" },
            { label: t("sort.jobName"), value: "Job Name" },
          ]}
        />
      </Grid>

      <Grid item xs={12}>
        <Grid container spacing={2}>
          {isTrainingJobLoading &&
            tabValue === "success" &&
            [1, 2].map((item: number) => (
              <Grid item xs={12} md={6} lg={4} key={item}>
                <SuccessfulTrainingCardLoader />
              </Grid>
            ))}
          {isTrainingJobLoading &&
            tabValue === "inProgress" &&
            [1, 2].map((item: number) => (
              <Grid item xs={12} md={6} lg={4} key={item}>
                <PendingFailedTrainingCardLoader cardType="pending" />
              </Grid>
            ))}
          {isTrainingJobLoading &&
            tabValue === "failed" &&
            [1, 2].map((item: number) => (
              <Grid item xs={12} md={6} lg={4} key={item}>
                <PendingFailedTrainingCardLoader cardType="failed" />
              </Grid>
            ))}

          {tabValue === "success" &&
            !isTrainingLoading &&
            !isRetrainigLoading &&
            !isTrainingAutoMlLoading &&
            adjustedAllSuccessfulTrainingJobs &&
            adjustedAllSuccessfulTrainingJobs?.length > 0 &&
            (adjustedAllSuccessfulTrainingJobs as Array<TrainingAndRetraining>)
              ?.slice(0, currentPage * pageSize)
              .map((item: TrainingAndRetraining, index: number) => {
                return (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    lg={4}
                    key={item.uuid}
                    style={{
                      paddingTop: "0px !important",
                      marginBottom: index === adjustedAllSuccessfulTrainingJobs?.length - 1 ? 16 : 0,
                    }}
                    ref={
                      adjustedAllSuccessfulTrainingJobs.length > currentPage * pageSize &&
                      index === currentPage * pageSize - 1
                        ? lastTrainingJobRef
                        : undefined
                    }
                  >
                    <SuccessfulTrainingJobCard
                      isLoading={isTrainingJobLoading}
                      uuid={item.uuid}
                      status={item.status}
                      startedAt={item?.started_at as string}
                      createdAt={item?.created_at}
                      createdModelName={item.resulting_model_name as string}
                      parentModel={item?.model as string}
                      userName={item.created_by}
                      duration={item.duration as string}
                      endedAt={item.ended_at as string}
                      trainingDataId={item.training_data as string}
                      resultingModelId={item.resulting_model as string}
                      dataPoints={
                        item.metrics
                          ? `${item.metrics?.split.train * 100}% / ${item.metrics?.split.test * 100}%`
                          : undefined
                      }
                      createdModelStatus={item.resulting_model_state as string}
                      projectType={cleanType}
                      firstPredictionDate={item.predictions_start_time as string}
                      lastPredictionDate={item.predictions_end_time as string}
                      trainingOrRetraining={
                        item?.training && item.hasOwnProperty("threshold_metrics") ? "training-automl" : "retraining"
                      }
                      evalMetrics={item.metrics ? item.metrics?.evaluation : undefined}
                      points={
                        item.metrics ? item.metrics?.split.train * 100 + item.metrics?.split.test * 100 : undefined
                      }
                    />
                  </Grid>
                )
              })}
          {!isTrainingLoading &&
            !isRetrainigLoading &&
            !isTrainingAutoMlLoading &&
            tabValue === "success" &&
            adjustedAllSuccessfulTrainingJobs &&
            adjustedAllSuccessfulTrainingJobs?.length === 0 &&
            ((succeededTrainingJobs && succeededTrainingJobs?.length > 0) ||
              (succeededRetrainingJobs && succeededRetrainingJobs?.length > 0)) && (
              <Grid className={styles.emptySearch} direction="column" container xs={12} item>
                <KonanEmptyStateSearch title={searchValue} />
              </Grid>
            )}
          {!isTrainingLoading &&
            !isRetrainigLoading &&
            !isTrainingAutoMlLoading &&
            tabValue === "success" &&
            (trainingModels || retrainingModels) &&
            allSucceededTrainingJobs?.length === 0 && (
              <Grid container item xs={12} className={"empty-container"}>
                <KonanEmptyState
                  title={t("empty_states.success.title")}
                  buttonText={currentProject?.type === "credit_scoring" ? "" : t("empty_states.success.buttonText")}
                  subTitle={t("empty_states.success.subtitle")}
                  setAction={setNewTrainingJobOpen}
                />
              </Grid>
            )}
          {tabValue === "inProgress" &&
            !isTrainingLoading &&
            !isRetrainigLoading &&
            !isTrainingAutoMlLoading &&
            adjustedAllPendingTrainingJobs &&
            adjustedAllPendingTrainingJobs?.length > 0 &&
            (adjustedAllPendingTrainingJobs as Array<TrainingAndRetraining>)
              ?.slice(0, currentPage * pageSize)
              .map((item: TrainingAndRetraining, index: number) => (
                <Grid
                  item
                  xs={12}
                  md={6}
                  lg={4}
                  key={item.uuid}
                  style={{ paddingTop: "0px !important" }}
                  ref={
                    adjustedAllPendingTrainingJobs.length > currentPage * pageSize &&
                    index === currentPage * pageSize - 1
                      ? lastTrainingJobRef
                      : undefined
                  }
                >
                  <PendingFailedTrainingJobCard
                    isLoading={isTrainingJobLoading}
                    uuid={item.uuid}
                    status={item.status}
                    startedAt={item.started_at as string}
                    createdAt={item.created_at}
                    createdModelName={item.resulting_model_name as string}
                    parentModel={item.model as string}
                    userName={item.created_by}
                    duration={item.duration as string}
                    predictions={item.predictions as string}
                    liveModel={currentModel}
                    cardType="pending"
                    endedAt={item.ended_at as string}
                    trainingDataId={item.training_data as string}
                    dataPoints={
                      item.metrics
                        ? `${item.metrics?.split.train * 100}% / ${item.metrics?.split.test * 100}%`
                        : undefined
                    }
                    createdModelStatus={item.resulting_model_state as string}
                    projectType={cleanType}
                    firstPredictionDate={item.predictions_start_time as string}
                    lastPredictionDate={item.predictions_end_time as string}
                    trainingOrRetraining={
                      item?.training
                        ? item.hasOwnProperty("threshold_metrics")
                          ? "training-automl"
                          : "training-prebuilt"
                        : "retraining"
                    }
                  />
                </Grid>
              ))}
          {!isTrainingJobLoading &&
            tabValue === "inProgress" &&
            adjustedAllPendingTrainingJobs?.length === 0 &&
            ((pendingTrainingJobs && pendingTrainingJobs?.length > 0) ||
              (pendingRetrainingJobs && pendingRetrainingJobs?.length > 0)) && (
              <Grid container item xs={12} className={styles.emptySearch}>
                <KonanEmptyStateSearch title={searchValueInProgress} />
              </Grid>
            )}
          {!isTrainingJobLoading &&
            tabValue === "inProgress" &&
            (trainingModels || retrainingModels) &&
            allPendingTrainingJobs?.length === 0 && (
              <Grid container xs={12} item className={"empty-container"}>
                <KonanEmptyState
                  title={t("empty_states.inProgress.title")}
                  buttonText={currentProject?.type === "credit_scoring" ? "" : t("empty_states.inProgress.buttonText")}
                  subTitle={t("empty_states.inProgress.subtitle")}
                  setAction={setNewTrainingJobOpen}
                />
              </Grid>
            )}
          {tabValue === "failed" &&
            !isTrainingLoading &&
            !isRetrainigLoading &&
            !isTrainingAutoMlLoading &&
            (failedRetrainingJobs || failedTrainingJobs) &&
            adjustedAllFailedTrainingJobs &&
            adjustedAllFailedTrainingJobs?.length > 0 &&
            (adjustedAllFailedTrainingJobs as Array<TrainingAndRetraining>)
              ?.slice(0, currentPage * pageSize)
              .map((item: TrainingAndRetraining, index: number) => (
                <Grid
                  item
                  xs={12}
                  md={6}
                  lg={4}
                  key={item.uuid}
                  style={{
                    paddingTop: "0px !important",
                    marginBottom: index === adjustedAllFailedTrainingJobs?.length - 1 ? 16 : 0,
                  }}
                  ref={
                    adjustedAllFailedTrainingJobs.length > currentPage * pageSize &&
                    index === currentPage * pageSize - 1
                      ? lastTrainingJobRef
                      : undefined
                  }
                >
                  <PendingFailedTrainingJobCard
                    isLoading={isTrainingJobLoading}
                    uuid={item.uuid}
                    status={item.status}
                    startedAt={item.started_at as string}
                    createdAt={item.created_at}
                    createdModelName={item.resulting_model_name as string}
                    parentModel={item.model as string}
                    userName={item.created_by}
                    duration={item.duration as string}
                    liveModel={currentModel}
                    predictions={item.predictions as string}
                    cardType="failed"
                    endedAt={item.ended_at as string}
                    trainingDataId={item.training_data as string}
                    dataPoints={
                      item.metrics
                        ? `${item.metrics?.split.train * 100}% / ${item.metrics?.split.test * 100}%`
                        : undefined
                    }
                    createdModelStatus={item.resulting_model_state as string}
                    projectType={cleanType}
                    firstPredictionDate={item.predictions_start_time as string}
                    lastPredictionDate={item.predictions_end_time as string}
                    trainingOrRetraining={
                      item?.training
                        ? item.hasOwnProperty("threshold_metrics")
                          ? "training-automl"
                          : "training-prebuilt"
                        : "retraining"
                    }
                  />
                </Grid>
              ))}
          {!isTrainingLoading &&
            !isRetrainigLoading &&
            !isTrainingAutoMlLoading &&
            tabValue === "failed" &&
            adjustedAllFailedTrainingJobs?.length === 0 &&
            ((failedTrainingJobs && failedTrainingJobs?.length > 0) ||
              (failedRetrainingJobs && failedRetrainingJobs?.length > 0)) && (
              <Grid container item xs={12} className={styles.emptySearch}>
                <KonanEmptyStateSearch title={searchValueFailed} />
              </Grid>
            )}
          {!isTrainingJobLoading &&
            tabValue === "failed" &&
            (trainingModels || retrainingModels) &&
            allFailedTrainingJobs?.length === 0 && (
              <Grid container xs={12} item className={"empty-container"}>
                <KonanEmptyState
                  title={t("empty_states.failed.title")}
                  buttonText={currentProject?.type === "credit_scoring" ? "" : t("empty_states.failed.buttonText")}
                  subTitle={t("empty_states.failed.subtitle")}
                  setAction={setNewTrainingJobOpen}
                />
              </Grid>
            )}

          {hasJobsLeft ? (
            <Grid container direction="column" justifyContent="center" alignItems="center" spacing={1} mt={3} mb={1}>
              <CircularProgress size={36} />
            </Grid>
          ) : (
            ""
          )}
        </Grid>
      </Grid>
    </Fragment>
  )
}
